/**
 * TeX maths → readable, editable text, the way Word's "linear format" writes
 * an equation: x³ − 3x² + 1, √5, (x + 3a)/(x − 3a), a ≠ b, 60°, OP ⊥ AM.
 *
 * The OCR writes a formula as inline TeX (`$\frac{1}{x^3}$`). The HTML document
 * view renders that with KaTeX, but the PDF editor sets text on a canvas, one
 * run after another — a stacked fraction cannot sit inside a line there. So for
 * the editor each formula becomes ordinary characters: superscripts and
 * subscripts as their Unicode forms where every character has one, fractions on
 * one line with brackets where they are needed, and the symbols as symbols. It
 * stays text — searchable, editable, exported as text.
 *
 * Malformed TeX (a `\frac` missing its second part, a `\sqrt` with nothing
 * after it) degrades to what is there rather than throwing.
 */

const SUP = {
    0: '⁰', 1: '¹', 2: '²', 3: '³', 4: '⁴', 5: '⁵', 6: '⁶', 7: '⁷', 8: '⁸', 9: '⁹',
    '+': '⁺', '−': '⁻', '-': '⁻', '=': '⁼', '(': '⁽', ')': '⁾',
    a: 'ᵃ', b: 'ᵇ', c: 'ᶜ', d: 'ᵈ', e: 'ᵉ', f: 'ᶠ', g: 'ᵍ', h: 'ʰ', i: 'ⁱ', j: 'ʲ', k: 'ᵏ', l: 'ˡ', m: 'ᵐ',
    n: 'ⁿ', o: 'ᵒ', p: 'ᵖ', r: 'ʳ', s: 'ˢ', t: 'ᵗ', u: 'ᵘ', v: 'ᵛ', w: 'ʷ', x: 'ˣ', y: 'ʸ', z: 'ᶻ',
    '°': '°', ' ': '',
};
const SUB = {
    0: '₀', 1: '₁', 2: '₂', 3: '₃', 4: '₄', 5: '₅', 6: '₆', 7: '₇', 8: '₈', 9: '₉',
    '+': '₊', '−': '₋', '-': '₋', '=': '₌', '(': '₍', ')': '₎',
    a: 'ₐ', e: 'ₑ', h: 'ₕ', i: 'ᵢ', j: 'ⱼ', k: 'ₖ', l: 'ₗ', m: 'ₘ', n: 'ₙ', o: 'ₒ', p: 'ₚ', r: 'ᵣ',
    s: 'ₛ', t: 'ₜ', u: 'ᵤ', v: 'ᵥ', x: 'ₓ', ' ': '',
};
// Bangla digits in an exponent read as the same digits.
const BN_DIGIT = { '০': '0', '১': '1', '২': '2', '৩': '3', '৪': '4', '৫': '5', '৬': '6', '৭': '7', '৮': '8', '৯': '9' };

const SYMBOLS = {
    neq: '≠', ne: '≠', leq: '≤', le: '≤', geq: '≥', ge: '≥', ll: '≪', gg: '≫',
    times: '×', div: '÷', pm: '±', mp: '∓', cdot: '·', ast: '∗', star: '⋆',
    approx: '≈', equiv: '≡', sim: '∼', simeq: '≃', cong: '≅', propto: '∝',
    infty: '∞', partial: '∂', nabla: '∇', degree: '°', prime: '′',
    angle: '∠', measuredangle: '∡', triangle: '△', square: '□', bot: '⊥', perp: '⊥', parallel: '∥',
    therefore: '∴', because: '∵',
    in: '∈', notin: '∉', ni: '∋', subset: '⊂', supset: '⊃', subseteq: '⊆', supseteq: '⊇',
    cup: '∪', cap: '∩', emptyset: '∅', varnothing: '∅', forall: '∀', exists: '∃', neg: '¬',
    land: '∧', lor: '∨', to: '→', rightarrow: '→', leftarrow: '←', Rightarrow: '⇒', Leftarrow: '⇐',
    leftrightarrow: '↔', Leftrightarrow: '⇔', implies: '⇒', iff: '⇔', mapsto: '↦',
    sum: 'Σ', prod: 'Π', int: '∫', oint: '∮',
    ldots: '…', cdots: '⋯', dots: '…', vdots: '⋮',
    alpha: 'α', beta: 'β', gamma: 'γ', delta: 'δ', epsilon: 'ε', varepsilon: 'ε', zeta: 'ζ', eta: 'η',
    theta: 'θ', vartheta: 'ϑ', iota: 'ι', kappa: 'κ', lambda: 'λ', mu: 'μ', nu: 'ν', xi: 'ξ',
    pi: 'π', rho: 'ρ', sigma: 'σ', tau: 'τ', upsilon: 'υ', phi: 'φ', varphi: 'φ', chi: 'χ',
    psi: 'ψ', omega: 'ω',
    Gamma: 'Γ', Delta: 'Δ', Theta: 'Θ', Lambda: 'Λ', Xi: 'Ξ', Pi: 'Π', Sigma: 'Σ', Phi: 'Φ',
    Psi: 'Ψ', Omega: 'Ω',
    circ: '∘', bullet: '•', backslash: '\\', '%': '%', '$': '$', '&': '&', '#': '#', _: '_',
    '{': '{', '}': '}', ',': ' ', ';': ' ', ':': ' ', '!': '', ' ': ' ', quad: ' ', qquad: '  ',
};
// Named functions print as their names, then a space before what they apply to.
const FUNCTIONS = new Set([
    'sin', 'cos', 'tan', 'cot', 'sec', 'csc', 'cosec', 'arcsin', 'arccos', 'arctan',
    'sinh', 'cosh', 'tanh', 'log', 'ln', 'lg', 'exp', 'lim', 'max', 'min', 'det', 'gcd', 'deg',
]);
// Wrappers whose content is simply the text.
const PASS = new Set(['text', 'mathrm', 'mathbf', 'mathit', 'mathsf', 'textbf', 'textit', 'operatorname', 'boldsymbol', 'mbox', 'displaystyle', 'textstyle']);
const ACCENT = { overline: '̅', bar: '̄', vec: '⃗', hat: '̂', dot: '̇', tilde: '̃' };

function script(text, table, mark) {
    const chars = Array.from(text.replace(/[০-৯]/g, (d) => BN_DIGIT[d]));
    if (chars.length && chars.every((c) => table[c] != null)) return chars.map((c) => table[c]).join('');
    // Not every character has a raised/lowered form: write it out.
    return chars.length === 1 ? `${mark}${text}` : `${mark}(${text})`;
}

/** A fraction's part needs brackets on one line when it is more than one plain term. */
const bracket = (part) => (/^[\p{L}\p{N}.√⁰¹²³⁴⁵⁶⁷⁸⁹°′]+$/u.test(part) || /^\(.*\)$/.test(part) ? part : `(${part})`);

// `s[i]` past the end is undefined, and /[a-z]/.test(undefined) tests the STRING
// "undefined" — true — so every letter scan must test the character itself.
const isLetter = (ch) => ch != null && /[a-zA-Z]/.test(ch);

function convert(tex) {
    let i = 0;
    const s = tex;

    const readGroup = () => {
        // `{…}` → its converted content; otherwise one token.
        while (s[i] === ' ') i += 1;
        if (s[i] === '{') {
            let depth = 1;
            const start = i + 1;
            i += 1;
            while (i < s.length && depth) {
                if (s[i] === '\\') { i += 2; continue; }
                if (s[i] === '{') depth += 1;
                else if (s[i] === '}') depth -= 1;
                i += 1;
            }
            return convert(s.slice(start, depth ? s.length : i - 1));
        }
        if (s[i] === '\\') {
            const start = i;
            i += 1;
            if (isLetter(s[i])) { while (isLetter(s[i])) i += 1; } else i += 1;
            return convert(s.slice(start, i));
        }
        if (i < s.length) { i += 1; return s[i - 1] === '-' ? '−' : s[i - 1]; }
        return null;
    };
    const hasGroup = () => { let j = i; while (s[j] === ' ') j += 1; return j < s.length && !/[}=+\-,;)]/.test(s[j]); };

    let out = '';
    while (i < s.length) {
        const c = s[i];
        if (c === '\\') {
            i += 1;
            let name = '';
            if (isLetter(s[i])) {
                while (isLetter(s[i])) { name += s[i]; i += 1; }
                // A space after a command name only ends the name, as in TeX.
                while (s[i] === ' ') i += 1;
            } else { name = s[i] || ''; i += 1; }

            if (name === 'frac' || name === 'dfrac' || name === 'tfrac' || name === 'cfrac') {
                const num = hasGroup() ? readGroup() : null;
                const den = hasGroup() ? readGroup() : null;
                if (num != null && den != null) out += `${bracket(num.trim())}/${bracket(den.trim())}`;
                else if (num != null) out += num; // half a fraction: keep what was written
            } else if (name === 'sqrt') {
                let index = '';
                if (s[i] === '[') {
                    const end = s.indexOf(']', i);
                    if (end > i) { index = convert(s.slice(i + 1, end)); i = end + 1; }
                }
                const arg = hasGroup() ? readGroup() : '';
                const root = index ? script(index, SUP, '') : '';
                out += `${root}√${arg ? bracket(arg.trim()) : ''}`;
            } else if (name === 'left' || name === 'right' || name === 'big' || name === 'Big' || name === 'bigg' || name === 'Bigg') {
                // Sizing only; the delimiter after it is printed as itself.
                if (s[i] === '.') i += 1;
            } else if (PASS.has(name)) {
                out += hasGroup() ? readGroup() : '';
            } else if (ACCENT[name]) {
                const arg = hasGroup() ? readGroup() : '';
                out += Array.from(arg).map((ch) => ch + ACCENT[name]).join('');
            } else if (FUNCTIONS.has(name)) {
                out += name === 'csc' ? 'cosec' : name;
                // "cot A", "tan θ" — but "sin²x" and "log(x)" keep close.
                if (s[i] && s[i] !== '(' && s[i] !== '^' && s[i] !== '_') out += ' ';
            } else if (SYMBOLS[name] != null) {
                out += SYMBOLS[name];
            } else {
                out += name; // unknown command: its name is the best guess at what it meant
            }
        } else if (c === '^' || c === '_') {
            i += 1;
            const arg = readGroup() ?? '';
            if (c === '^' && /^\s*(∘|°)\s*$/.test(arg)) out += '°';
            else if (c === '^' && /^\s*′+\s*$/.test(arg)) out += arg.trim();
            else out += script(arg, c === '^' ? SUP : SUB, c);
        } else if (c === '{' || c === '}') {
            const g = c === '{' ? readGroup() : (i += 1, '');
            out += g ?? '';
        } else if (c === '-') {
            out += '−';
            i += 1;
        } else if (c === '~') {
            out += ' ';
            i += 1;
        } else {
            out += c;
            i += 1;
        }
    }
    return out;
}

/** One TeX formula (no `$`) → text. */
export function texToText(tex) {
    try {
        return convert(String(tex ?? ''))
            // Relations read with a space either side: "a ≠ b", "OP ⊥ AM", "x = 5.5".
            .replace(/\s*([=≠≤≥≈≡≅≃∼∝⊥∥∈∉⊂⊃⊆⊇→←⇒⇐⇔↔])\s*/g, ' $1 ')
            .replace(/ {2,}/g, ' ')
            .trim();
    } catch {
        return String(tex ?? '');
    }
}

// Pandoc's rule, as the rest of the AI-document code reads maths (utils/aiDocMath): the
// opening `$` is followed by a non-space, the closing one preceded by a non-space and not
// followed by a digit — so "costs $5 and $10" is money, not maths.
const MATH = /\$\$([\s\S]+?)\$\$|\$(?=\S)([^$\n]*?\S)\$(?!\d)/g;
// TeX the OCR wrote without its `$`s, recognised by the commands only maths uses.
const LOOSE_TEX = /\\(frac|dfrac|sqrt|neq|leq|geq|times|div|pm|circ|bot|perp|angle|triangle|theta|alpha|beta|pi|infty|therefore|because|cdot)\b/;

/** Every `$…$` formula in a line of text → readable text; the rest is left alone. */
export function mathToText(line) {
    const text = String(line ?? '');
    let out = '';
    let last = 0;
    const plain = (seg) => (LOOSE_TEX.test(seg) ? texToText(seg) : seg);
    if (text.includes('$')) {
        for (const m of text.matchAll(MATH)) {
            out += plain(text.slice(last, m.index)) + texToText(m[1] ?? m[2]);
            last = m.index + m[0].length;
        }
    }
    return out + plain(text.slice(last));
}
